import { CheckCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import React, { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { SingleImage } from 'app/components/commons/Images/SingleImage/SingleImage';
import type { PictureView } from 'app/typings/core/common/pictureView/pictureView';

import styles from './TableCellRenderers.module.scss';

export const ImageTextCell = ({
  images,
  title,
  subtitle,
  tooltip,
}: {
  images?: PictureView[];
  title: string;
  subtitle?: string;
  tooltip?: string;
}) => (
  <div className={styles.imageTextCell}>
    <SingleImage
      image={
        images?.[0] || {
          path: 'path1',
          alt: 'alt1',
          id: 1,
          createdAt: new Date(),
          updatedAt: new Date(),
          dimensions: { width: 100, height: 100 },
        }
      }
      width={40}
      height={40}
    />

    <div className={styles.cellContent}>
      <div className={styles.cellTitle}>
        {title}
        {!!tooltip && (
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined />
          </Tooltip>
        )}
      </div>
      {subtitle && <div className={styles.cellSubtitle}>{subtitle}</div>}
    </div>
  </div>
);

export const TextCell = ({
  value,
  variant,
}: {
  value?: string | React.ReactNode;
  variant?: 'grey' | 'default';
}) => (
  <span className={variant === 'grey' ? styles.notAllowed : undefined}>
    {value || <span className={styles.notAllowed}>-</span>}
  </span>
);

export const PriceCell = ({ value }: { value?: number }) => {
  const { currencyFormatter } = useContext(CurrencyContext);

  if (value === undefined) {
    return <span className={styles.notAllowed}>-</span>;
  }

  return <span>{currencyFormatter(value)}</span>;
};

export const TagCell = ({
  values,
  color = 'default',
  icon,
  onIconClick,
}: {
  values?: (number | string | undefined)[];
  color?: 'green' | 'red' | 'default';
  icon?: React.ReactNode;
  onIconClick?: () => void;
}) => {
  if (!values || values.length === 0) {
    return <span className={styles.notAllowed}>-</span>;
  }

  return (
    <div className={styles.tagCell}>
      {values.map((value, index) => {
        if (value === undefined) {
          return null;
        }

        return (
          <Tag
            key={index}
            color={color}
            className={classNames(styles.tag, {
              [styles.clickableTag]: !!onIconClick,
            })}
            onClick={onIconClick}
          >
            {value}
            {icon}
          </Tag>
        );
      })}
    </div>
  );
};

export const BadgeCell = () => (
  <CheckCircleFilled style={{ color: '#52C41A' }} />
);
